import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowsLeftRight, MagnifyingGlass, ArrowSquareOut, XCircle, DownloadSimple } from "@phosphor-icons/react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import FeedPartnerSelect from "@/components/FeedPartnerSelect";
import SuggestRoutesPanel from "@/pages/admin/SuggestRoutesPanel";
import RoutedPerformancePanel from "@/pages/admin/RoutedPerformancePanel";

const NETWORK_COLORS = {
  impact: "bg-blue-50 text-blue-700",
  partnerize: "bg-indigo-50 text-indigo-700",
  skimlinks: "bg-orange-50 text-orange-700",
  mrge: "bg-emerald-50 text-emerald-700",
  cj: "bg-amber-50 text-amber-700",
  pubadhub: "bg-cyan-50 text-cyan-700",
  pubadhub_kevin: "bg-teal-50 text-teal-700",
  admedia: "bg-rose-50 text-rose-700",
  flexoffers: "bg-violet-50 text-violet-700",
  shopnomix_kevin: "bg-fuchsia-50 text-fuchsia-700",
  siteplug: "bg-sky-50 text-sky-700",
  giddyup: "bg-teal-50 text-teal-800",
  yieldkit: "bg-fuchsia-50 text-fuchsia-700",
  shopnomix: "bg-lime-50 text-lime-700",
  pronomix: "bg-purple-50 text-purple-700",
  maxbounty: "bg-cyan-50 text-cyan-700",
  cashbackhive: "bg-teal-50 text-teal-700",
};

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "inactive", label: "Inactive" },
  { value: "unassigned", label: "Unassigned" },
];

function NetworkPill({ network }) {
  const n = (network || "").toLowerCase();
  if (!n) return <span className="text-slate-400 text-xs">—</span>;
  const cls = NETWORK_COLORS[n] || "bg-slate-100 text-slate-700";
  const lbl = n === "pubadhub_kevin" ? "pah-kevin" : n;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${cls}`}>
      {lbl}
    </span>
  );
}

function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const cls = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    paused: "bg-amber-50 text-amber-700 border-amber-200",
    inactive: "bg-rose-50 text-rose-700 border-rose-200",
    unassigned: "bg-slate-50 text-slate-500 border-slate-200",
  }[s] || "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${cls}`}>
      {s || "—"}
    </span>
  );
}

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function rowKey(row) {
  return `${row.publisher_id}:${row.from_brand_id}`;
}

export default function AdminRoutedBrands() {
  const [tab, setTab] = useState("active"); // active | suggest
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [disabling, setDisabling] = useState(false);

  const [pubQ, setPubQ] = useState("");
  const [brandQ, setBrandQ] = useState("");
  const [pubQInput, setPubQInput] = useState("");
  const [brandQInput, setBrandQInput] = useState("");
  const [fromNetwork, setFromNetwork] = useState("all");
  const [toNetwork, setToNetwork] = useState("all");
  const [brandStatus, setBrandStatus] = useState("all");
  const [routedBrandStatus, setRoutedBrandStatus] = useState("all");
  const [sort, setSort] = useState("updated");
  const [selected, setSelected] = useState(() => new Map());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState([]);

  /** Filters shared by the table and the CSV export, so the file always
   *  matches what is on screen. Pagination is deliberately excluded —
   *  the export returns every matching route, not the current page. */
  const filterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (pubQ.trim()) params.set("publisher_q", pubQ.trim());
    if (brandQ.trim()) params.set("brand_q", brandQ.trim());
    if (fromNetwork && fromNetwork !== "all") params.set("from_network", fromNetwork);
    if (toNetwork && toNetwork !== "all") params.set("to_network", toNetwork);
    if (brandStatus !== "all") params.set("brand_status", brandStatus);
    if (routedBrandStatus !== "all") params.set("routed_brand_status", routedBrandStatus);
    params.set("sort", sort || "updated");
    return params;
  }, [pubQ, brandQ, fromNetwork, toNetwork, brandStatus, routedBrandStatus, sort]);

  const [exporting, setExporting] = useState(false);

  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const res = await API.get(
        `/admin/traffic-remaps/export?${filterParams().toString()}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      a.download = `routed-brands-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${total.toLocaleString()} route${total === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not export routed brands");
    } finally {
      setExporting(false);
    }
  }, [filterParams, total]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterParams();
      params.set("page", String(page));
      params.set("page_size", "50");
      const { data } = await API.get(`/admin/traffic-remaps?${params.toString()}`);
      setItems(data?.items || []);
      setTotal(data?.total || 0);
      setTotalPages(data?.total_pages || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load routed brands");
    } finally {
      setLoading(false);
    }
  }, [filterParams, page]);

  useEffect(() => {
    if (tab === "active") fetchRows();
  }, [fetchRows, tab]);

  useEffect(() => {
    const id = setTimeout(() => {
      setPubQ(pubQInput);
      setPage(1);
      setSelected(new Map());
    }, 300);
    return () => clearTimeout(id);
  }, [pubQInput]);
  useEffect(() => {
    const id = setTimeout(() => {
      setBrandQ(brandQInput);
      setPage(1);
      setSelected(new Map());
    }, 300);
    return () => clearTimeout(id);
  }, [brandQInput]);
  useEffect(() => {
    setPage(1);
    setSelected(new Map());
  }, [fromNetwork, toNetwork, brandStatus, routedBrandStatus, sort]);

  const pageKeys = items.map(rowKey);
  const allPageSelected = pageKeys.length > 0 && pageKeys.every((k) => selected.has(k));
  const somePageSelected = pageKeys.some((k) => selected.has(k));

  const toggleRow = (row, checked) => {
    const key = rowKey(row);
    setSelected((prev) => {
      const next = new Map(prev);
      if (checked) next.set(key, row);
      else next.delete(key);
      return next;
    });
  };

  const toggleSelectAll = (checked) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (checked) items.forEach((r) => next.set(rowKey(r), r));
      else items.forEach((r) => next.delete(rowKey(r)));
      return next;
    });
  };

  const askRemove = (rows) => {
    const list = (rows || []).filter((r) => r?.publisher_id && r?.from_brand_id);
    if (!list.length) return;
    setPendingRemove(list);
    setConfirmOpen(true);
  };

  const confirmRemove = async () => {
    const list = pendingRemove;
    if (!list.length) {
      setConfirmOpen(false);
      return;
    }
    setDisabling(true);
    try {
      const { data } = await API.post("/admin/traffic-remaps/disable-bulk", {
        items: list.map((r) => ({
          publisher_id: r.publisher_id,
          from_brand_id: r.from_brand_id,
        })),
      });
      const ok = data?.removed ?? (typeof data?.ok === "number" ? data.ok : list.length);
      toast.success(`Removed ${ok} route${ok === 1 ? "" : "s"}`);
      setSelected(new Map());
      setConfirmOpen(false);
      setPendingRemove([]);
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed to disable route");
    } finally {
      setDisabling(false);
    }
  };

  const selectedCount = selected.size;
  const confirmCount = pendingRemove.length;
  const confirmLabel = confirmCount === 1
    ? `Remove routing for ${pendingRemove[0]?.from_brand_name || pendingRemove[0]?.from_brand_id} on ${pendingRemove[0]?.publisher_email || "this publisher"}?`
    : `Remove ${confirmCount} selected routes? Old tracker URLs will stop redirecting to the target network.`;

  return (
    <div className="p-8" data-testid="admin-routed-brands-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-1">
          <ArrowsLeftRight size={14} />
          Audit / Tracking
        </div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Routed Brands</h1>
        <p className="text-sm text-slate-500 mt-1">
          Active traffic remaps, plus suggest &amp; route by domain+geo across networks.
        </p>
      </div>

      <div className="mb-5 flex items-center gap-1 border-b border-slate-200">
        <button
          type="button"
          onClick={() => setTab("active")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === "active"
              ? "border-slate-900 text-slate-900"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
          data-testid="routed-brands-tab-active"
        >
          Active routes
        </button>
        <button
          type="button"
          onClick={() => setTab("suggest")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === "suggest"
              ? "border-slate-900 text-slate-900"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
          data-testid="routed-brands-tab-suggest"
        >
          Suggest routes
        </button>
        <button
          type="button"
          onClick={() => setTab("performance")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === "performance"
              ? "border-slate-900 text-slate-900"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
          data-testid="routed-brands-tab-performance"
        >
          Routed performance
        </button>
      </div>

      {tab === "performance" ? (
        <RoutedPerformancePanel />
      ) : tab === "suggest" ? (
        <SuggestRoutesPanel onApplied={() => setTab("active")} />
      ) : (
      <>
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Publisher email / sub_id…"
            value={pubQInput}
            onChange={(e) => setPubQInput(e.target.value)}
            className="h-9 text-sm pl-8 w-56"
            data-testid="routed-brands-pub-search"
          />
        </div>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Brand name or id…"
            value={brandQInput}
            onChange={(e) => setBrandQInput(e.target.value)}
            className="h-9 text-sm pl-8 w-56"
            data-testid="routed-brands-brand-search"
          />
        </div>
        <FeedPartnerSelect
          value={fromNetwork}
          onChange={setFromNetwork}
          testId="routed-brands-from-network"
          allLabel="All from networks"
          menuLabel="Network (from)"
        />
        <FeedPartnerSelect
          value={toNetwork}
          onChange={setToNetwork}
          testId="routed-brands-to-network"
          allLabel="All to networks"
          menuLabel="Routed network (to)"
        />
        <select
          value={brandStatus}
          onChange={(e) => setBrandStatus(e.target.value)}
          className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
          data-testid="routed-brands-brand-status"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              Brand: {o.label}
            </option>
          ))}
        </select>
        <select
          value={routedBrandStatus}
          onChange={(e) => setRoutedBrandStatus(e.target.value)}
          className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
          data-testid="routed-brands-routed-status"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              Routed brand: {o.label}
            </option>
          ))}
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
          data-testid="routed-brands-sort"
        >
          <option value="updated">Sort: Recently updated</option>
          <option value="clicks_desc">Sort: Clicks (high → low)</option>
        </select>
        <Button
          variant="outline"
          size="sm"
          className="h-9 text-sm"
          onClick={exportCsv}
          disabled={exporting || total === 0}
          title={total ? `Download all ${total.toLocaleString()} matching routes as CSV` : "Nothing to export"}
          data-testid="routed-brands-export-csv"
        >
          <DownloadSimple size={14} className="mr-1" />
          {exporting ? "Exporting…" : "Export CSV"}
        </Button>
        {selectedCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="h-9 text-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            disabled={disabling}
            onClick={() => askRemove([...selected.values()])}
            data-testid="routed-brands-remove-selected"
          >
            <XCircle size={14} className="mr-1" />
            Remove {selectedCount} selected
          </Button>
        )}
      </div>

      <div className="text-xs text-slate-500 mb-2 font-mono" data-testid="routed-brands-summary">
        {total.toLocaleString()} route{total === 1 ? "" : "s"}
        {selectedCount > 0 ? ` · ${selectedCount} selected` : ""}
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="routed-brands-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-2.5 w-10">
                  <Checkbox
                    checked={allPageSelected ? true : somePageSelected ? "indeterminate" : false}
                    onCheckedChange={(v) => toggleSelectAll(v === true)}
                    aria-label="Select all on this page"
                    className="border-slate-400"
                    data-testid="routed-brands-select-all"
                    disabled={loading || items.length === 0}
                  />
                </th>
                <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Brand</th>
                <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Publisher</th>
                <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">SubId</th>
                <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Network (from)</th>
                <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Routed network (to)</th>
                <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Brand status</th>
                <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Routed brand status</th>
                <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Routed from</th>
                <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  <button
                    type="button"
                    className={`uppercase tracking-wider font-bold ${sort === "clicks_desc" ? "text-slate-800" : "text-slate-500 hover:text-slate-700"}`}
                    onClick={() => setSort("clicks_desc")}
                    title="Sort by clicks, high to low"
                    data-testid="routed-brands-sort-clicks"
                  >
                    After routed clicks{sort === "clicks_desc" ? " ↓" : ""}
                  </button>
                </th>
                <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-slate-400 text-sm">Loading…</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-slate-400 text-sm">
                    No routed brands match the current filters.
                  </td>
                </tr>
              ) : items.map((row) => {
                const key = rowKey(row);
                const checked = selected.has(key);
                return (
                  <tr key={key} data-testid={`routed-brands-row-${row.from_brand_id}`}>
                    <td className="px-3 py-2.5">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => toggleRow(row, v === true)}
                        aria-label={`Select ${row.from_brand_name || row.from_brand_id}`}
                        className="border-slate-400"
                        data-testid={`routed-brands-check-${row.from_brand_id}`}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-slate-900">{row.from_brand_name || "—"}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{row.from_brand_id}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="text-slate-800 truncate max-w-[200px]">{row.publisher_email || "—"}</div>
                      {row.publisher_company ? (
                        <div className="text-[11px] text-slate-400 truncate max-w-[200px]">{row.publisher_company}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5 text-xs font-mono text-slate-600">{row.publisher_sub_id || "—"}</td>
                    <td className="px-3 py-2.5"><NetworkPill network={row.from_network} /></td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <NetworkPill network={row.to_network} />
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 truncate max-w-[180px]">
                        {row.to_brand_name || row.to_brand_id}
                      </div>
                    </td>
                    <td className="px-3 py-2.5"><StatusPill status={row.brand_status} /></td>
                    <td className="px-3 py-2.5"><StatusPill status={row.routed_brand_status} /></td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{fmtWhen(row.routed_from)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs font-semibold text-slate-800">
                      {(row.routed_clicks || 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={disabling}
                          onClick={() => askRemove([row])}
                          data-testid={`routed-brands-remove-${row.from_brand_id}`}
                        >
                          <XCircle size={14} className="mr-1" />
                          Remove
                        </Button>
                        <Link
                          to={`/admin/publishers/${row.publisher_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center px-2 h-7 text-xs text-slate-400 hover:text-slate-700"
                          title="Open publisher detail"
                        >
                          <ArrowSquareOut size={14} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-between text-xs text-slate-500" data-testid="routed-brands-pagination">
          <span className="font-mono">Page {page} of {totalPages} · {total.toLocaleString()} total</span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="routed-brands-prev">
              Prev
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="routed-brands-next">
              Next
            </Button>
          </div>
        </div>
      )}

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (disabling) return;
          setConfirmOpen(open);
          if (!open) setPendingRemove([]);
        }}
      >
        <AlertDialogContent data-testid="routed-brands-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmCount === 1 ? "Remove this route?" : `Remove ${confirmCount} routes?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmLabel}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disabling} data-testid="routed-brands-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              disabled={disabling}
              onClick={(e) => {
                e.preventDefault();
                confirmRemove();
              }}
              data-testid="routed-brands-confirm-remove"
            >
              {disabling ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </>
      )}
    </div>
  );
}
